// @flow
import * as React from 'react';
import BlockUserModal, { OpenBlockUserModal } from './BlockUserModal';
import UnblockUserModal, { OpenUnblockUserModal } from './UnblockUserModal';

/** Provides a modal and a way to open it */

type UserProfile = {
  userId: string,
  displayName: string,
};
/** Interaction types */
export type InteractionType = 'BLOCK' | 'UNBLOCK';

// Specify the type so Flow knows to infer
const { Provider, Consumer } = React.createContext<(type: InteractionType) => void>(() => {
  throw new Error('Attempted to open a modal without an enclosing MemberInteractionProvider');
});

type ExternalProps = {|
  /** A member to interact with */
  member: UserProfile,
  /** A callback to fire when the modal closes */
  onClose?: () => void,
  /** An optional callback when the the submission completes */
  onSubmit?: () => void,
  /** Children */
  children: React.Node,
|};

type Props = {|
  /** The props exposed to consumers of the Provider */
  ...ExternalProps,
  /** Internal: The namespace generated to deduplicate modals */
  namespace: string,
  /** Internal: Opens the block user modal. Provided by <OpenBlockUserModal/> */
  openBlockUserModal: () => mixed,
  /** Internal: Opens the unblock user modal. Provided by <OpenUnblockUserModal/> */
  openUnblockUserModal: () => mixed,
|};

/** A base wrapper for a React.Provider which will ensure uniqueness of member interaction modals */
class MemberInteractionProvider extends React.Component<Props> {
  openModal = (type: InteractionType): void => {
    if (type === 'BLOCK') {
      this.props.openBlockUserModal();
    } else if (type === 'UNBLOCK') {
      this.props.openUnblockUserModal();
    } else {
      throw new Error(`Attempted to open type ${type} but it does not exist.`);
    }
  };

  render() {
    const {
      namespace,
      member: { userId, displayName },
      onClose,
      onSubmit,
      children,
    } = this.props;
    return (
      <Provider value={this.openModal}>
        <BlockUserModal
          userIdToBlock={userId}
          displayName={displayName}
          namespace={namespace}
          onClose={onClose}
          onSubmit={onSubmit}
          key={`BLOCK_MODAL:${userId}:${namespace}`}
        />
        <UnblockUserModal
          userIdToUnblock={userId}
          displayName={displayName}
          namespace={namespace}
          onClose={onClose}
          onSubmit={onSubmit}
          key={`UNBLOCK_MODAL:${userId}:${namespace}`}
        />
        {children}
      </Provider>
    );
  }
}

// eslint-disable-next-line react/no-multi-comp,ta-code-conventions/require-jsdoc
class WrappedMemberInteractionProvider extends React.Component<ExternalProps> {
  namespace: string = `MEMBER_INTERACTION:${Math.random()}`;

  render() {
    const { userId } = this.props.member;
    return (
      <OpenBlockUserModal userId={userId} namespace={this.namespace}>
        {openBlockUserModal => (
          <OpenUnblockUserModal userId={userId} namespace={this.namespace}>
            {openUnblockUserModal => (
              <MemberInteractionProvider
                {...this.props}
                namespace={this.namespace}
                openUnblockUserModal={openUnblockUserModal}
                openBlockUserModal={openBlockUserModal}
              />
            )}
          </OpenUnblockUserModal>
        )}
      </OpenBlockUserModal>
    );
  }
}

export {
  MemberInteractionProvider as BaseMemberInteractionProvider,
  WrappedMemberInteractionProvider as MemberInteractionProvider,
  Consumer as MemberInteractionConsumer,
};
